import Link from '@/components/core/router-link';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from '@/lib/react-router-compat';
import StepVerticalTrackBar from '@/components/stepper/vertical-track-bar';
import StepHorizontalTrackBar from '@/components/stepper/horizontal-track-bar';
import StepperProvider, { useStepper } from '@/components/stepper/stepper-provider';
import { Steps } from '@/components/stepper/stepper-models';
import { CreateApiBasicInfoForm } from './forms/create-api-basic-info-form';
import { CreateApiInputParametersForm } from './forms/create-api-input-parameters-form';
import { CreateApiOutputParametersForm } from './forms/create-api-output-parameters-form';
import { CreateApiDebuggingForm } from './forms/create-api-debugging-form';
import { CreateApiProvider, useCreateApiContext } from './create-api-context';
import { useGetToolDetails } from '@/modules/gpt-chats/hooks/use-tools';
import { useTranslation } from 'react-i18next';

export const CreateAPIWrapper = ({
  toolId,
  initialStep,
}: {
  toolId: string;
  initialStep?: number;
}) => {
  const { t } = useTranslation();
  const steps = useMemo<Steps>(
    () => [
      { id: 1, title: t('BASIC_INFORMATION') },
      { id: 2, title: t('INPUT_PARAMETERS') },
      { id: 3, title: t('OUTPUT_PARAMETERS') },
      { id: 4, title: t('DEBUGGING') },
    ],
    [t]
  );

  return (
    <CreateApiProvider>
      <StepperProvider steps={steps} initialStep={initialStep}>
        <CreateAPI toolId={toolId} />
      </StepperProvider>
    </CreateApiProvider>
  );
};

export const CreateAPI = ({ toolId }: { toolId: string }) => {
  const searchParams = useSearchParams();
  const actionId = searchParams.get('actionId');
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const { currentStep } = useStepper();
  const { t } = useTranslation();
  const { setFormData } = useCreateApiContext();
  const { data: toolDetails } = useGetToolDetails(toolId, isBlocksTool);
  const [isDataReady, setIsDataReady] = useState(false);

  useEffect(() => {
    if (actionId) {
      if (toolDetails?.APIConfig?.Actions) {
        const action = toolDetails.APIConfig.Actions[actionId];

        if (action) {
          setFormData({
            actionId: actionId,
            name: action.Name,
            description: action.Description || '',
            method: action.Method,
            endpoint: action.Endpoint,
            input_parameters: action.InputParameters || {},
            output_parameters: action.OutputParameters || {},
          });
          setIsDataReady(true);
        }
      }
    } else {
      setIsDataReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, toolDetails]);

  if (!isDataReady) {
    return null; // Or a loading spinner
  }

  return (
    <>
      {/* mobile design */}
      <div className="flex flex-col md:hidden">
        <div className="mt-16 flex-1 p-5">
          <div className="flex flex-col items-center justify-center md:hidden">
            <div className="flex gap-2">
              <Link href={`/ai/tool-details/${toolId}/api?isBlocksTool=${isBlocksTool}`}>
                <X size={32} strokeWidth={1} />
              </Link>
              <p className="mt-[2px] text-lg font-semibold">{t('CREATE_API')}</p>
            </div>
            <p className="mb-7 mt-2 text-sm font-normal text-medium-emphasis">
              {t('CREATE_API_DESCRIPTION')}
            </p>
            <StepHorizontalTrackBar />
          </div>
        </div>
        <div className="p-5">
          {currentStep === 1 && <CreateApiBasicInfoForm key={actionId || 'new'} toolId={toolId} />}
          {currentStep === 2 && <CreateApiInputParametersForm />}
          {currentStep === 3 && <CreateApiOutputParametersForm toolId={toolId} />}
          {currentStep === 4 && <CreateApiDebuggingForm />}
        </div>
      </div>

      {/* desktop design */}
      <div className="hidden gap-12 px-10 md:flex">
        <div className="min-h-screen max-w-80 gap-5 bg-background p-5 pt-24 bg-gray-100 dark:bg-gray-900">
          <div className="mx-2 my-3">
            <div className="flex gap-2">
              <Link href={`/ai/tool-details/${toolId}/api?isBlocksTool=${isBlocksTool}`}>
                <X size={32} strokeWidth={1} />
              </Link>
              <p className="mt-[2px] text-lg font-semibold">{t('CREATE_API')}</p>
            </div>
            <p className="mb-7 mt-2 text-sm font-normal text-medium-emphasis">
              {t('CREATE_API_DESCRIPTION')}
            </p>
          </div>
          <StepVerticalTrackBar />
        </div>
        <div className="mt-24 w-full">
          {currentStep === 1 && <CreateApiBasicInfoForm key={actionId || 'new'} toolId={toolId} />}
          {currentStep === 2 && <CreateApiInputParametersForm />}
          {currentStep === 3 && <CreateApiOutputParametersForm toolId={toolId} />}
          {currentStep === 4 && <CreateApiDebuggingForm />}
        </div>
      </div>
    </>
  );
};
